import { ChangeEvent, forwardRef, TextareaHTMLAttributes, useId, useState } from 'react';
import { clsx } from 'clsx';

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  /** Helper text (Flutter `helperText`). Hidden while an error is shown. */
  hint?: string;
  /** Error text (Flutter `errorText`). */
  error?: string;
}

/**
 * Multiline sibling of Input: same filled + outlined look, notched floating label, helper/error
 * line and disabled colors. With `maxLength` it adds Flutter's default "n/max" counter at the
 * bottom-right (bodySmall onSurfaceVariant, error color once over the limit).
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    { label, hint, error, className, disabled, id, placeholder, rows = 3, maxLength, value, defaultValue, onChange, ...props },
    ref
  ) => {
    const generatedId = useId();
    const areaId = id || generatedId;
    const hintId = `${areaId}-hint`;
    const errorId = `${areaId}-error`;
    const hasError = !!error;

    // Controlled fields are counted from `value`; uncontrolled ones from what was typed.
    const [typedLength, setTypedLength] = useState(String(defaultValue ?? '').length);
    const length = value !== undefined ? String(value).length : typedLength;
    const overLimit = maxLength !== undefined && length > maxLength;

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
      setTypedLength(e.target.value.length);
      onChange?.(e);
    };

    return (
      <div data-testid="input-root" className={clsx('w-full', className)}>
        <div className="relative">
          <textarea
            ref={ref}
            id={areaId}
            rows={rows}
            maxLength={maxLength}
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
            disabled={disabled}
            // A non-empty placeholder is what lets CSS tell "empty" from "has a value".
            placeholder={placeholder || ' '}
            aria-describedby={clsx(hint && !hasError && hintId, hasError && errorId) || undefined}
            aria-invalid={hasError}
            className={clsx(
              'peer relative block w-full resize-none rounded-xl bg-lm-surface-container px-3 py-4 text-body-lg text-lm-on-surface caret-lm-primary outline-none',
              'placeholder:text-transparent focus:placeholder:text-lm-on-surface-variant',
              'disabled:text-lm-on-surface/38'
            )}
            {...props}
          />

          {/* Notched border. The empty legend collapses to nothing until the label floats. */}
          <fieldset
            aria-hidden="true"
            data-testid="input-outline"
            className={clsx(
              'pointer-events-none absolute inset-x-0 bottom-0 -top-[5px] m-0 min-w-0 rounded-xl border px-2',
              hasError
                ? 'border-lm-error peer-focus:border-lm-error'
                : 'border-lm-outline-variant peer-focus:border-lm-primary',
              'peer-disabled:border-lm-on-surface/12',
              'peer-focus:[&>legend]:max-w-full peer-[:not(:placeholder-shown)]:[&>legend]:max-w-full'
            )}
          >
            <legend className="invisible block h-[11px] max-w-[0.01px] whitespace-nowrap p-0 text-[12px] transition-[max-width] duration-100">
              <span className="px-1">{label}</span>
            </legend>
          </fieldset>

          {label && (
            <label
              htmlFor={areaId}
              className={clsx(
                // At rest the label sits on the first line (28px, like Input's centered label).
                'pointer-events-none absolute left-3 top-7 origin-left -translate-y-1/2 whitespace-nowrap text-body-lg transition-all duration-150',
                'peer-focus:top-0 peer-focus:scale-75',
                'peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:scale-75',
                hasError
                  ? 'text-lm-error peer-focus:text-lm-error'
                  : 'text-lm-on-surface-variant peer-focus:text-lm-primary',
                'peer-disabled:text-lm-on-surface/38'
              )}
            >
              {label}
            </label>
          )}
        </div>

        {(hasError || hint || maxLength !== undefined) && (
          <div className="mt-1 flex gap-4 px-3">
            {hasError ? (
              <p id={errorId} className="text-body-sm text-lm-error" role="alert">
                {error}
              </p>
            ) : (
              hint && (
                <p id={hintId} className="text-body-sm text-lm-on-surface-variant" role="status">
                  {hint}
                </p>
              )
            )}
            {maxLength !== undefined && (
              <span
                data-testid="textarea-counter"
                className={clsx('ml-auto text-body-sm', overLimit ? 'text-lm-error' : 'text-lm-on-surface-variant')}
              >
                {`${length}/${maxLength}`}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';
